import { AxiosError } from 'axios'
import { ParseKeys } from 'i18next'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { openBlobFile } from '~/shared/lib/file/openBlobFile'
import { logger } from '~/shared/lib/logger'
import { mapMessageBlobErrors } from '~/shared/lib/mapMessageErrors'
import { snackbarEnqueued } from '~/shared/lib/notifications'
import { myPalette } from '~/shared/lib/theme'
import { Button, IconButton } from '~/shared/ui/Buttons'
import { FileIcon, PrintIcon } from '~/shared/ui/Icons'

type TitleOnly = {
  title: string
  titleI18n?: never
}

type TitleI18nOnly = {
  title?: never
  titleI18n: ParseKeys
}

type RequiredTitle = TitleOnly | TitleI18nOnly

type FileExportBlobProps = {
  fn: () => Promise<Blob> | void | Blob
  target?: '_self' | '_blank' | '_parent' | '_top'
  isIcon?: boolean
} & RequiredTitle

export function FileExportBlob({
  fn,
  titleI18n,
  title,
  target = '_self',
  isIcon,
}: FileExportBlobProps) {
  const { t } = useTranslation()
  const [isLoading, toggleIsLoading] = useState(false)

  const handlePrintFile = useCallback(async () => {
    try {
      toggleIsLoading(true)
      const response = await fn()

      if (!response) return

      openBlobFile(response, target)
    } catch (e) {
      logger.error(e)
      const errorMessage = await mapMessageBlobErrors(e as AxiosError<Blob>)

      snackbarEnqueued({
        message: errorMessage,
        variant: 'error' as const,
      })
    } finally {
      toggleIsLoading(false)
    }
  }, [fn, target])

  if (isIcon) {
    return (
      <IconButton
        onClick={handlePrintFile}
        loading={isLoading}
        tooltipProps={{ title: title ?? t(titleI18n) }}
      >
        <PrintIcon />
      </IconButton>
    )
  }

  return (
    <Button
      startDecorator={<FileIcon color={myPalette.brand['700']} />}
      onClick={handlePrintFile}
      loading={isLoading}
      variant='gray'
    >
      {title ?? t(titleI18n)}
    </Button>
  )
}
